import type {
  CreateEcnBody,
  EcnSummaryDto,
  ListEcnRobotsResponse,
  ListEcnsResponse,
  UpdateEcnBody,
} from '@bhops/shared';
import { apiClient } from './client';

// S2 Phase 2.3 — ECN(제조 변경점) 마스터 카탈로그 typed client.
// models.ts 와 동일한 apiClient 래핑 패턴.

export interface ListEcnsParams {
  /** description / code 부분 일치 검색어. */
  q?: string;
  /** 'all' = 비활성 포함. 미지정 = 활성만. */
  active?: 'all';
  /**
   * 이 모델에 걸린 변경점만. **전 모델 공통(models 빈 배열) 변경점은 항상 함께**
   * 내려온다 — 필터를 걸어도 공통 변경점이 사라지면 안 되므로.
   */
  modelId?: string;
  /**
   * 개선계획 §4 — 새 로봇 등록 창에서 미리 체크된 상태로 제안할 변경점만
   * (Ecn.autoApplyOnRegistration). 등록 폼의 기본 후보 계산에만 쓴다.
   */
  autoApply?: boolean;
}

export interface HardDeleteEcnResult {
  id: string;
  deleted: true;
  /** 사유에 이 ECN 코드를 적어둔 서비스 오더 수 — 문자열 참조라 고아가 된다. */
  referencedServiceOrderCount: number;
}

export const ecnsApi = {
  async list(params: ListEcnsParams = {}): Promise<ListEcnsResponse> {
    const { data } = await apiClient.get<ListEcnsResponse>('/ecns', {
      params: {
        ...(params.q ? { q: params.q } : {}),
        ...(params.active ? { active: params.active } : {}),
        ...(params.modelId ? { modelId: params.modelId } : {}),
        ...(params.autoApply ? { autoApply: 'true' } : {}),
      },
    });
    return data;
  },

  async create(body: CreateEcnBody): Promise<EcnSummaryDto> {
    const { data } = await apiClient.post<EcnSummaryDto>('/ecns', body);
    return data;
  },

  async get(id: string): Promise<EcnSummaryDto> {
    const { data } = await apiClient.get<EcnSummaryDto>(`/ecns/${encodeURIComponent(id)}`);
    return data;
  },

  async update(id: string, body: UpdateEcnBody): Promise<EcnSummaryDto> {
    const { data } = await apiClient.patch<EcnSummaryDto>(`/ecns/${encodeURIComponent(id)}`, body);
    return data;
  },

  async remove(id: string): Promise<EcnSummaryDto> {
    const { data } = await apiClient.delete<EcnSummaryDto>(`/ecns/${encodeURIComponent(id)}`);
    return data;
  },

  // 완전삭제 — 보관된 변경점만, 적용 이력 0건일 때만(서버 가드). 되돌릴 수 없다.
  async hardDelete(id: string): Promise<HardDeleteEcnResult> {
    const { data } = await apiClient.delete<HardDeleteEcnResult>(
      `/ecns/${encodeURIComponent(id)}/permanent`,
    );
    return data;
  },

  // 역추적(recall) — ECN → 적용 로봇 목록.
  //
  // S3 Phase 3.5 — `operatingOnly` 면 운영 중(DEPLOYED) 로봇만. 각 행에 status +
  // 활성 Deployment 의 고객사/매장이 추가로 담겨 온다 (현장 부품 추적용).
  async listRobots(
    id: string,
    params: { operatingOnly?: boolean } = {},
  ): Promise<ListEcnRobotsResponse> {
    const { data } = await apiClient.get<ListEcnRobotsResponse>(
      `/ecns/${encodeURIComponent(id)}/robots`,
      { params: params.operatingOnly ? { operatingOnly: 'true' } : {} },
    );
    return data;
  },
};
